import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  FiCheck, FiCopy, FiFacebook, FiLink, FiSend, FiShare2, FiTwitter, FiX,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { cShare, API_BASE } from '../../api';
import { useLanguage } from '../../i18n';
import { BLUE } from './brand';

/**
 * Share button → a proper share sheet (not a tiny dropdown).
 *
 * The sheet always shows a big, visible **Copy this post** button first, the
 * post URL, then the social targets (Facebook / Telegram / X / WhatsApp) and
 * the device share sheet when the browser supports it.
 *
 * The link that gets shared is the API's **preview page**
 * (`/share/post/<id>`): Facebook / Telegram fetch it, read its Open Graph tags
 * and show the article **picture (with the title on it) + title + description**,
 * then the visitor is taken to `/post/<id>` on the website.
 */
export default function ShareMenu({ post, onChange }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [count, setCount] = useState(post?.share_count || 0);

  const site = typeof window !== 'undefined' ? window.location.origin : '';
  // The shared link must look like **this website**, never the API host:
  //   · production → the site origin, and netlify.toml proxies /share/* to the API
  //   · localhost  → the dev server, and package.json proxies /share to :8000
  // The /share/post/<id> page is what Facebook / Telegram read for the Open Graph
  // picture + title; it then sends the visitor to /post/<id> on the website.
  const shareBase = process.env.REACT_APP_SHARE_URL || site || API_BASE;
  const url = `${shareBase}/share/post/${post.id}?to=${encodeURIComponent(site || shareBase)}`;
  const cover = `${shareBase}/share/post/${post.id}/cover.jpg`;
  const text = post.title || t('community');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const count_ = async (channel) => {
    try {
      const res = await cShare(post.id, channel);
      setCount(res.share_count);
      if (onChange) onChange(res);
    } catch (e) { /* sharing still works offline */ }
  };

  const copy = async () => {
    count_('copy');
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('linkCopied'));
    } catch (e) {
      // Older browsers / non-secure origins: fall back to a temporary input.
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); toast.success(t('linkCopied')); } catch (e2) { toast(url); }
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: text, text, url });
      count_('native');
      setOpen(false);
    } catch (e) { /* the user closed the sheet */ }
  };


  const targets = [
    {
      key: 'facebook',
      icon: <FiFacebook />,
      label: t('shareFacebook'),
      color: '#1877F2',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
      key: 'telegram',
      icon: <FiSend />,
      label: t('shareTelegram'),
      color: '#229ED9',
      href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    },
    {
      key: 'whatsapp',
      icon: <FaWhatsapp />,
      label: t('shareWhatsapp'),
      color: '#25D366',
      href: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
    },
    {
      key: 'x',
      icon: <FiTwitter />,
      label: 'X',
      color: '#111827',
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    },
  ];

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold text-xs sm:text-sm"
      >
        <FiShare2 className="w-4 h-4" /> {t('share')}
        {count > 0 && <span className="text-gray-400">{count}</span>}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[999] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm anim-fade-in" onClick={() => setOpen(false)} />

          <div className="relative w-full sm:max-w-md m-0 sm:m-4 rounded-t-3xl sm:rounded-3xl bg-white dark:bg-gray-900 shadow-2xl anim-modal overflow-hidden">
            <header className="flex items-center justify-between px-5 pt-4 pb-2">
              <h3 className="font-extrabold text-base dark:text-gray-100">{t('sharePost')}</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label={t('close')}
              >
                <FiX className="w-5 h-5" />
              </button>
            </header>

            <div className="px-5 pb-5 space-y-4">
              {/* What Facebook / Telegram will show: picture + title + description */}
              <div className="rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                <img
                  src={cover}
                  alt={text}
                  className="w-full aspect-[1200/630] object-cover bg-gray-100 dark:bg-gray-800"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800/60">
                  <p className="text-[10px] font-bold uppercase text-gray-400 truncate">
                    {(() => { try { return new URL(url).host; } catch (e) { return ''; } })()}
                  </p>
                  <p className="text-sm font-bold truncate dark:text-gray-100">{text}</p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2">
                    {post.description || t('communityTagline')}
                  </p>
                </div>
              </div>
              <p className="text-[11px] text-gray-400 -mt-1">🖼️ {t('sharePreviewHint')}</p>

              {/* The post link + the big, always-visible copy button */}
              <div className="flex items-center gap-2 rounded-2xl border border-gray-200 dark:border-gray-700 p-2">
                <FiLink className="w-4 h-4 text-gray-400 shrink-0" />
                <input
                  readOnly
                  value={url}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-0 bg-transparent text-xs text-gray-600 dark:text-gray-300 truncate"
                />
              </div>

              <button
                type="button"
                onClick={copy}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl text-white font-bold text-sm shadow-lg hover:brightness-110"
                style={{ background: copied ? '#16a34a' : BLUE }}
              >
                {copied ? <FiCheck className="w-5 h-5" /> : <FiCopy className="w-5 h-5" />}
                {copied ? t('linkCopied') : t('copyThisPost')}
              </button>

              {(post?.external_share !== false) && (
                <>
                  <div>
                    <p className="text-[11px] font-bold text-gray-400 uppercase mb-2">{t('shareTo')}</p>
                    <div className="grid grid-cols-4 gap-2">
                      {targets.map((tg) => (
                        <a
                          key={tg.key}
                          href={tg.href}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => { count_(tg.key); setOpen(false); }}
                          className="flex flex-col items-center gap-1.5 py-3 rounded-2xl border border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <span className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg" style={{ background: tg.color }}>
                            {tg.icon}
                          </span>
                          <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300">{tg.label}</span>
                        </a>
                      ))}
                    </div>
                  </div>

                  {typeof navigator !== 'undefined' && !!navigator.share && (
                    <button
                      type="button"
                      onClick={nativeShare}
                      className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-2xl font-bold text-sm border-2"
                      style={{ borderColor: BLUE, color: BLUE }}
                    >
                      <FiShare2 className="w-4 h-4" /> {t('shareNative')}
                    </button>
                  )}
                </>
              )}

              {/* ជម្រើសនៃការចែករំលែក — the author only allows the plain link */}
              {(post?.external_share === false) && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {t('shareToSocialHint')}
                </p>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

